import { getApolloClient } from '@faustwp/core';
import { SEO } from '../../components';
import { Main } from '../../components';
import {
  Header,
  Footer,
  HeroSection,
  ServicesSection,
  FeaturedWorkSection,
  TechnologySection,
  TeamCultureSection,
  BlogPreviewSection,
  FinalCTASection,
} from '../../components/sections';
import {
  GET_FRONT_PAGE_DATA,
  FRONT_PAGE_VARIABLES,
  GET_FEATURED_CASE_IMAGES_FROM_POSTS,
  getFeaturedCaseImageUrls,
  mapRecentPostsToPreview,
} from '../../lib/queries/frontPage';
import { fetchPageSettings } from '../../lib/queries/pageSettings';
import { fetchLogoPosts } from '../../lib/logoUtils';
import { fetchBlogListData } from '../../lib/blogUtils';
import { getFeaturedCaseImageUrlsFromRest } from '../../lib/wordpressRest';
import { getCanonicalUrl, buildHreflangTags } from '../../lib/siteUrl';

const FALLBACK_FI = {
  metaTitle: 'Identio | Luotettava kumppanisi ohjelmisto- ja digikehityksessä',
  metaDescription:
    'Luotettava kumppanisi ohjelmisto- ja digikehityksessä. Liitymme luontevasti osaksi kehitystiimiäsi. Tuomme mielenrauhaa ja mitattavia tuloksia liiketoimintaanne.',
  heroTitle: 'Luotettava kumppanisi ohjelmisto- ja digikehityksessä.',
  heroDescription:
    'Liitymme luontevasti osaksi kehitystiimiäsi. Tuomme mielenrauhaa ja mitattavia tuloksia liiketoimintaanne.',
  heroPrimaryCta: 'Ota yhteyttä',
  heroPrimaryCtaUrl: '/yhteys',
  heroSecondaryCta: 'Työmme',
  heroSecondaryCtaUrl: '/tyomme',
};

export default function EtusivuPage({
  homeLogos = [],
  featuredCaseImageUrls = {},
  blogPosts = [],
  etusivuSettings = null,
}) {
  const s = etusivuSettings ?? {};
  const title = s.seoTitle ?? s.metaTitle ?? s.meta_title ?? FALLBACK_FI.metaTitle;
  const description = s.seoDescription ?? s.metaDescription ?? s.meta_description ?? FALLBACK_FI.metaDescription;
  const heroTitle = s.heroTitle ?? s.hero_title ?? FALLBACK_FI.heroTitle;
  const heroDescription = s.heroDescription ?? s.hero_description ?? FALLBACK_FI.heroDescription;
  const primaryCta = s.heroPrimaryCta ?? s.hero_primary_cta ?? FALLBACK_FI.heroPrimaryCta;
  const primaryHref = s.heroPrimaryCtaUrl ?? s.hero_primary_cta_url ?? FALLBACK_FI.heroPrimaryCtaUrl;
  const secondaryCta = s.heroSecondaryCta ?? s.hero_secondary_cta ?? FALLBACK_FI.heroSecondaryCta;
  const secondaryHref = s.heroSecondaryCtaUrl ?? s.hero_secondary_cta_url ?? FALLBACK_FI.heroSecondaryCtaUrl;

  return (
    <>
      <SEO title={title} description={description} url={getCanonicalUrl('/')} alternates={buildHreflangTags('/', '/en')} />
      <Header />
      <Main>
        <HeroSection
          variant="home"
          logos={homeLogos}
          title={heroTitle}
          description={heroDescription}
          primaryCta={primaryCta}
          primaryHref={primaryHref}
          secondaryCta={secondaryCta}
          secondaryHref={secondaryHref}
        />
        <ServicesSection variant="home" />
        <FeaturedWorkSection featuredCaseImageUrls={featuredCaseImageUrls} />
        <TechnologySection variant="home" />
        <TeamCultureSection />
        <BlogPreviewSection posts={blogPosts} />
        <FinalCTASection variant="simple" />
      </Main>
      <Footer />
    </>
  );
}

export async function getStaticProps() {
  const client = getApolloClient();
  let homeLogos = [];
  let featuredCaseImageUrls = {};
  let blogPosts = [];

  try {
    await client.query({
      query: GET_FRONT_PAGE_DATA,
      variables: FRONT_PAGE_VARIABLES,
    });
  } catch (err) {
    console.error('[etusivu/index] getStaticProps menu/settings query failed', err);
  }

  let etusivuSettings = null;
  try {
    etusivuSettings = await fetchPageSettings(client, 'etusivu');
  } catch (e) {
    // ACF optional
  }

  try {
    const [{ logos }, featuredData, blogData] = await Promise.all([
      fetchLogoPosts(client),
      client.query({ query: GET_FEATURED_CASE_IMAGES_FROM_POSTS }).then((r) => r.data).catch(() => ({})),
      fetchBlogListData(client, { first: 3 }, { lang: 'fi' }),
    ]);
    if (logos?.length) homeLogos = [...logos].reverse().slice(0, 8);
    featuredCaseImageUrls = getFeaturedCaseImageUrls(featuredData) ?? {};
    if (!featuredCaseImageUrls?.mehilainen && !featuredCaseImageUrls?.visma && !featuredCaseImageUrls?.smartum) {
      try {
        featuredCaseImageUrls = (await getFeaturedCaseImageUrlsFromRest()) ?? {};
      } catch {
        // REST fallback failed, keep empty
      }
    }
    const posts = blogData?.posts ?? [];
    blogPosts = mapRecentPostsToPreview(posts.slice(0, 3), '/blogi');
  } catch (err) {
    console.error('[etusivu/index] getStaticProps data failed', err);
  }

  return {
    props: {
      homeLogos,
      featuredCaseImageUrls,
      blogPosts,
      etusivuSettings,
    },
    revalidate: 60,
  };
}
